import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Creators as ReportActions } from "../../../store/ducks/report";
import { Creators as SupplierActions } from "../../../store/ducks/supplier";
// Components
import { Col, Row, Select, Spin, notification } from "antd";
import CustomDivider from "../../utils/customDivider";
import {
  Container,
  ExtensionButton,
  ExtensionContainer,
  Title,
} from "./styles";
// Services
import { Form, Formik } from "formik";
import moment from "moment";
import { getLotIndexDropDown } from "../../../services/lotService";
import { processReportLotStock } from "../../../services/reportService";
import ExportPdfIcon from "../../utils/icons/export/pdf";
import { findAllFarmOwners } from "../../../services/farmOwnerService";
import { getBreedIndexActive } from "../../../services/breedService";
import fileReader from "../../../utils/fileReader";

const INITIAL_FORM_STATE = {
  breedIds: [],
  lotIds: [],
  supplierIds: [],
  farmOwnerIds: [],
};

const ExportReportLotStockModal = () => {
  const formikRef = useRef();
  // Variables
  const [lotsOptions, setLotsOptions] = useState([]);
  const [breedOptions, setBreedOptions] = useState([]);
  const [farmOwnerOptions, setFarmOwnerOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Redux Variables
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const { isModalLotStockReportVisible: modalVisible } = useSelector(
    (state) => state.report
  );

  const { listDrowpDown: supplierOptions } = useSelector(
    (state) => state.supplier
  );

  const dispatch = useDispatch();

  // Methods
  const fetchLots = useCallback(async () => {
    const {
      data: { results },
    } = await getLotIndexDropDown({
      groupId,
      farmId,
    });
    setLotsOptions(
      results.filter(
        (lot) =>
          (lot.status === "Active" ||
            lot.status === "A" ||
            lot.status === "Pending" ||
            lot.status === "P") &&
          lot.currentAmountAnimals > 0
      )
    );
  }, [groupId, farmId]);

  const fetchSuppliers = useCallback(async () => {
    dispatch(
      SupplierActions.getDropdownSuppliers(
        groupId,
        farmId,
        "CattleFarmer",
        true
      )
    );
  }, [dispatch, farmId, groupId]);

  const fetchFarmOwners = useCallback(async () => {
    try {
      const {
        data: { results },
      } = await findAllFarmOwners({
        groupId,
        farmId,
      });

      setFarmOwnerOptions(results);
    } catch (error) {}
  }, [farmId, groupId]);

  const fetchBreeds = useCallback(async () => {
    const {
      data: { results },
    } = await getBreedIndexActive({
      groupId,
      farmId,
      withoutBreedCurveParam: true,
    });

    setBreedOptions(results);
  }, [farmId, groupId]);

  async function handleSubmit(values, options) {
    setIsLoading(true);

    notification.info({
      message:
        "O seu relatório está sendo preparado e logo será feito o download!",
    });

    const body = {
      ...values,
    };

    setTimeout(() => {
      setIsLoading(false);
      dispatch(ReportActions.hideModal());
    }, 100);

    try {
      const { data } = await processReportLotStock({
        groupId,
        farmId,
        body,
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([data]));
      let link = document.createElement("a");
      link.href = downloadUrl;
      const dayFormat = moment().format("YYYYMMDD");
      const hourFormat = moment().format("HHmm");
      link.setAttribute(
        "download",
        `Estoque_por_Lote_${dayFormat}_${hourFormat}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      notification.success({
        message: translation.successGenerateReport,
      });
    } catch (error) {
      let message = translation.errorGenerateReport;
      if (error?.response) {
        try {
          const { data } = error.response;
          // Read file
          const file = await fileReader(data);

          if (typeof file === "string") {
            // Parse content and retrieve 'message'
            const result = JSON.parse(file);
            if (result.code === 5035) {
              message =
                translation.exportReportLotStockModal.messages.errorNoDataFound;
            }
          }
        } catch (readError) {}
      }
      notification.error({
        message,
      });
    }
  }

  useEffect(() => {
    if (formikRef && formikRef.current && modalVisible) {
      formikRef.current.resetForm(INITIAL_FORM_STATE);
    }
    return () => {};
  }, [modalVisible]);

  useEffect(() => {
    if (modalVisible) {
      fetchLots();
      fetchBreeds();
      fetchFarmOwners();
      fetchSuppliers();
    }
    return () => {};
  }, [fetchBreeds, fetchFarmOwners, fetchLots, fetchSuppliers, modalVisible]);

  return (
    <Container
      width={600}
      visible={modalVisible}
      centered
      closable={true}
      footer={null}
      onCancel={() => dispatch(ReportActions.hideModal())}
    >
      <Spin spinning={isLoading}>
        <Row type="flex">
          <Col span={24}>
            <Title>{translation.exportReportLotStockModal.title}</Title>
          </Col>
        </Row>
        <Formik
          ref={formikRef}
          initialValues={INITIAL_FORM_STATE}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <Row gutter={16}>
                <Col span={12}>
                  <label>
                    {translation.exportReportLotStockModal.form.breedIds}
                  </label>
                  <Select
                    mode="multiple"
                    value={values.breedIds}
                    onChange={(val) => setFieldValue("breedIds", val)}
                    style={{ width: "100%" }}
                    placeholder="Selecione as raças"
                  >
                    {breedOptions.map((opt) => (
                      <Select.Option key={opt.id} value={opt.id}>
                        {opt.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <label>
                    {translation.exportReportLotStockModal.form.lotIds}
                  </label>
                  <Select
                    mode="multiple"
                    value={values.lotIds}
                    onChange={(val) => setFieldValue("lotIds", val)}
                    style={{ width: "100%" }}
                    placeholder="Selecione os lotes"
                  >
                    {lotsOptions.map((lot) => (
                      <Select.Option key={lot.id} value={lot.id}>
                        {lot.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <label>
                    {translation.exportReportLotStockModal.form.supplierIds}
                  </label>
                  <Select
                    mode="multiple"
                    value={values.supplierIds}
                    onChange={(val) => setFieldValue("supplierIds", val)}
                    style={{ width: "100%" }}
                    placeholder="Selecione os fornecedores"
                  >
                    {supplierOptions.map((opt) => (
                      <Select.Option key={opt.id} value={opt.id}>
                        {opt.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <label>
                    {translation.exportReportLotStockModal.form.farmOwnerIds}
                  </label>
                  <Select
                    mode="multiple"
                    value={values.farmOwnerIds}
                    onChange={(val) => setFieldValue("farmOwnerIds", val)}
                    style={{ width: "100%" }}
                    placeholder="Selecione os proprietários"
                  >
                    {farmOwnerOptions.map((opt) => (
                      <Select.Option key={opt.id} value={opt.id}>
                        {opt.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              <CustomDivider dashed title="EXTENSÃO" />
              <Row type="flex">
                <Col span={24}>
                  <ExtensionContainer>
                    <ExtensionButton type="submit">
                      <ExportPdfIcon />
                    </ExtensionButton>
                  </ExtensionContainer>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Spin>
    </Container>
  );
};

export default ExportReportLotStockModal;
